import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { AlertTriangle, Heart, Phone, Camera, PenTool } from 'lucide-react';
import { toast } from 'sonner';

export const ALLERGY_OPTIONS = [
  { id: 'peanuts', label: 'Peanuts' },
  { id: 'tree_nuts', label: 'Tree Nuts' },
  { id: 'dairy', label: 'Dairy' },
  { id: 'gluten', label: 'Gluten' },
  { id: 'eggs', label: 'Eggs' },
  { id: 'soy', label: 'Soy' },
  { id: 'shellfish', label: 'Shellfish' },
  { id: 'other', label: 'Other (specify below)' },
];

export const MEDICAL_OPTIONS = [
  { id: 'asthma', label: 'Asthma' },
  { id: 'epilepsy', label: 'Epilepsy' },
  { id: 'diabetes', label: 'Diabetes' },
  { id: 'adhd', label: 'ADHD' },
  { id: 'autism', label: 'Autism' },
  { id: 'none', label: 'None' },
];

const toSet = (arr) => new Set(arr || []);

// Module-scope so the component identity is stable across parent re-renders.
// Defining this *inside* AllergyFormCompact would create a new component type
// on every keystroke → React unmounts/remounts the inner <Input>/<Textarea>
// fields → loses focus & cursor after typing a single character.
const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-3">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#FF3B6B]" />
      <h3 className="font-semibold text-sm text-[#1E1E24]">{title}</h3>
    </div>
    {children}
  </div>
);

/**
 * Single-page allergy + consent form. Used by:
 *  - CRM (admin filling on behalf of guest)
 *  - Public RSVP page (guest filling via token)
 *  - Customer portal (host filling for a guest)
 *
 * Props:
 *  initial      — optional existing form payload (to edit)
 *  attendeeName — default child name
 *  onSubmit     — async (payload) => void
 *  onCancel     — () => void
 *  readOnly     — disable all inputs + show only a "Close" button
 *  submitLabel  — button text (default "Submit form")
 *  defaultSignatureName — prefill signature with admin/user name
 */
export const AllergyFormCompact = ({
  initial, attendeeName = '', onSubmit, onCancel, readOnly = false,
  submitLabel = 'Submit form', defaultSignatureName = '',
}) => {
  const [attendee, setAttendee] = useState(attendeeName || '');
  const [allergies, setAllergies] = useState(() => toSet(initial?.medical_data?.allergies));
  const [allergyOther, setAllergyOther] = useState(initial?.medical_data?.allergy_other || '');
  const [medical, setMedical] = useState(() => toSet(initial?.medical_data?.medical_conditions));
  const [medicalOther, setMedicalOther] = useState(initial?.medical_data?.medical_other || '');
  const [emergencyName, setEmergencyName] = useState(initial?.emergency_contact_name || '');
  const [emergencyPhone, setEmergencyPhone] = useState(initial?.emergency_contact_phone || '');
  const [emergencyRel, setEmergencyRel] = useState(initial?.emergency_contact_relationship || '');
  const [photoConsent, setPhotoConsent] = useState(
    initial?.photo_video_consent ?? true,
  );
  const [waiver, setWaiver] = useState(initial?.liability_waiver_accepted ?? false);
  const [signature, setSignature] = useState(initial?.signature_name || defaultSignatureName || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (attendeeName && !attendee) setAttendee(attendeeName);
  }, [attendeeName, attendee]);

  const toggle = (setter, set) => (id, checked) => {
    const next = new Set(set);
    if (checked) next.add(id); else next.delete(id);
    setter(next);
  };

  const canSubmit = attendee.trim().length > 0
    && emergencyName.trim().length > 0
    && emergencyPhone.trim().length > 0
    && waiver
    && signature.trim().length > 0;

  const submit = async () => {
    if (readOnly) return;
    if (!canSubmit) {
      toast.error('Please fill attendee name, emergency contact, waiver, and signature.');
      return;
    }
    setSaving(true);
    try {
      await onSubmit?.({
        attendee_name: attendee.trim(),
        allergies: Array.from(allergies),
        allergy_other: allergyOther,
        medical_conditions: Array.from(medical),
        medical_other: medicalOther,
        emergency_contact_name: emergencyName.trim(),
        emergency_contact_phone: emergencyPhone.trim(),
        emergency_contact_relationship: emergencyRel.trim(),
        photo_video_consent: !!photoConsent,
        liability_waiver_accepted: !!waiver,
        signature_name: signature.trim(),
        signature_date: new Date().toISOString().slice(0, 10),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="allergy-form-compact">
      <Section icon={AlertTriangle} title="Attending child's name">
        <Input
          value={attendee}
          onChange={(e) => setAttendee(e.target.value)}
          disabled={readOnly}
          placeholder="Full name of the child attending"
          className="rounded-xl border-[#E8E5DF]"
          data-testid="allergy-attendee-name"
        />
      </Section>

      <Section icon={AlertTriangle} title="Allergies">
        <div className="grid grid-cols-2 gap-2">
          {ALLERGY_OPTIONS.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={allergies.has(a.id)}
                disabled={readOnly}
                onCheckedChange={(c) => toggle(setAllergies, allergies)(a.id, c)}
                data-testid={`allergy-${a.id}`}
              />
              {a.label}
            </label>
          ))}
        </div>
        {allergies.has('other') && (
          <Textarea
            value={allergyOther}
            onChange={(e) => setAllergyOther(e.target.value)}
            disabled={readOnly}
            rows={2}
            placeholder="Please specify other allergies"
            className="rounded-xl border-[#E8E5DF]"
            data-testid="allergy-other-text"
          />
        )}
      </Section>

      <Section icon={Heart} title="Medical conditions">
        <div className="grid grid-cols-2 gap-2">
          {MEDICAL_OPTIONS.map((m) => (
            <label key={m.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={medical.has(m.id)}
                disabled={readOnly}
                onCheckedChange={(c) => toggle(setMedical, medical)(m.id, c)}
                data-testid={`medical-${m.id}`}
              />
              {m.label}
            </label>
          ))}
        </div>
        <Textarea
          value={medicalOther}
          onChange={(e) => setMedicalOther(e.target.value)}
          disabled={readOnly}
          rows={2}
          placeholder="Any other medical conditions or notes (optional)"
          className="rounded-xl border-[#E8E5DF]"
          data-testid="medical-other-text"
        />
      </Section>

      <Section icon={Phone} title="Emergency contact">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} disabled={readOnly} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="emergency-name" />
          </div>
          <div>
            <Label className="text-xs">Phone *</Label>
            <Input value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} disabled={readOnly} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="emergency-phone" />
          </div>
          <div>
            <Label className="text-xs">Relationship</Label>
            <Input value={emergencyRel} onChange={(e) => setEmergencyRel(e.target.value)} disabled={readOnly} placeholder="e.g. Parent" className="rounded-xl border-[#E8E5DF] mt-1" data-testid="emergency-rel" />
          </div>
        </div>
      </Section>

      <Section icon={Camera} title="Consent">
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={photoConsent}
            disabled={readOnly}
            onCheckedChange={(c) => setPhotoConsent(!!c)}
            data-testid="photo-consent"
          />
          <span>I consent to photos and videos being taken during the party and used for social media / promotional purposes.</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={waiver}
            disabled={readOnly}
            onCheckedChange={(c) => setWaiver(!!c)}
            data-testid="waiver-consent"
          />
          <span className="text-[13px]"><strong>Liability waiver (required):</strong> I accept the liability waiver and agree that Creative Birthdays staff may administer basic first aid or call emergency services if needed. *</span>
        </label>
      </Section>

      <Section icon={PenTool} title="Signature">
        <Label className="text-xs">Type your full name as signature *</Label>
        <Input
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          disabled={readOnly}
          placeholder="e.g. Jane Doe"
          className="rounded-xl border-[#E8E5DF] mt-1 font-[Fredoka] text-lg"
          data-testid="signature-name"
        />
        <p className="text-[11px] text-[#6B6B7A]">Signed on {new Date().toISOString().slice(0, 10)}.</p>
      </Section>

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} className="rounded-full" data-testid="allergy-cancel">
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
        )}
        {!readOnly && (
          <Button
            onClick={submit}
            disabled={saving || !canSubmit}
            className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full"
            data-testid="allergy-submit"
          >
            {saving ? 'Saving…' : submitLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AllergyFormCompact;
